// utils/getStationDisplayProps.ts
import SVG from "../components/svg/Svg"; // твои иконки

// ─── Константы состояний (убрать магические числа) ───
const STATION_OFFLINE = 0; // status_id: станция не на связи
const STATION_ONLINE = 1; // status_id: станция на связи

const STATE_FREE = 1;
const STATE_PREPARING = 2;
const STATE_CHARGING = 3;
const STATE_CHARGED = 4;
const STATE_UNAVAILABLE = 5; // админом выключена
const STATE_MODULE_OFFLINE = 6; // станция на связи, модуль нет (нет связи М)

// Интерфейс результата — всё, что нужно для отображения
type StationDisplayProps = {
  state: string; // текст состояния
  colorClass: string; // CSS-класс цвета
  icon: any;
  color: string; // иконка из SVG
  inactive: boolean;
  // `С2` (`ТЕСТ-В33`): подсказка под статусом. Есть только у состояния 2 —
  // у остальных её нет, и пустая строка не выводится.
  hint: string | null;
};

/**
 * `С2` (`ТЕСТ-В33`, после `FW-165`): состояние 2 — «подключено, заряд не начат
 * или приостановлен». Подпись «подготовка» читалась как «идёт подготовка к
 * заряду», хотя заряд может и не начаться: разрешение даёт сервер.
 */
export const STATION_WAITING_HINT =
  "Автомобиль подключён. Заряд начнётся, когда будет разрешение";

/** `B.13`: остановка лимитом линии — коды `station_diag` 3 и 11. */
export const STATION_LINE_LIMIT_PAUSED = "приостановлено — лимит линии";
const LINE_LIMIT_STOP_REASONS = new Set([3, 11]);

// Тип станции
type Station = {
  status_id: number; // 0 - оффлайн, 1 - онлайн
  state_id: number; // см. константы STATE_*
  gps: string | null; // строка с ошибкой → если не пустая → ошибка (legacy-название поля)
  /** `B.13`: последняя сессия заряда; `stop_reason` 3/11 — лимит линии. */
  last_session?: { stop_reason?: number | null } | null;
};

/**
 * Утверждённая иерархия приоритетов отображения:
 * 1. Нет связи (М)       — модуль не отвечает (state_id === 6)
 * 2. Нет связи           — станция полностью оффлайн (status_id === 0)
 * 3. Ошибка              — аппаратная ошибка (gps не пусто)
 * 4. Недоступно          — выключено административно (state_id === 5)
 * 5. Рабочие состояния   — свободно, подготовка, заряжается, заряжено
 */
export const getStationDisplayProps = (
  station: Station,
): StationDisplayProps => {
  // 1. Нет связи с модулем (М)
  if (station.state_id === STATE_MODULE_OFFLINE) {
    return {
      state: "нет связи (М)",
      colorClass: "color-grey",
      icon: SVG.station_unavailable,
      color: "var(--grey4)",
      inactive: true,
      hint: null,
    };
  }

  // 2. Станция полностью оффлайн
  if (station.status_id === STATION_OFFLINE) {
    return {
      state: "нет связи",
      colorClass: "color-grey",
      icon: SVG.station_noStatus,
      color: "var(--grey4)",
      inactive: true,
      hint: null,
    };
  }

  // 3. Ошибка оборудования (поле gps — legacy-название, означает аппаратную ошибку)
  if (station.gps && station.gps.trim() !== "") {
    return {
      state: "ошибка",
      colorClass: "color-grey",
      icon: SVG.station_err,
      color: "var(--red-darktheme)",
      inactive: true,
      hint: null,
    };
  }

  // 4. Административно недоступно
  if (station.state_id === STATE_UNAVAILABLE) {
    return {
      state: "недоступно",
      colorClass: "color-grey",
      icon: SVG.station_unavailable,
      color: "var(--grey4)",
      inactive: true,
      hint: null,
    };
  }

  // 5. Рабочие состояния
  switch (station.state_id) {
    case STATE_FREE:
      return {
        state: "свободно",
        colorClass: "color-green",
        icon: SVG.station_free,
        color: "var(--green)",
        inactive: false,
        hint: null,
      };
    case STATE_PREPARING: {
      const stopReason = Number(station.last_session?.stop_reason);

      // `B.13`: станция ждёт, но в этой же сессии её остановил лимит линии.
      if (LINE_LIMIT_STOP_REASONS.has(stopReason)) {
        return {
          state: STATION_LINE_LIMIT_PAUSED,
          colorClass: "color-orange",
          icon: SVG.station_preparing,
          color: "var(--logo)",
          inactive: false,
          hint: null,
        };
      }

      return {
        state: "ожидание заряда",
        colorClass: "color-orange",
        icon: SVG.station_preparing,
        // Шапка карточки остаётся как была: синим (--logo) по белому.
        color: "var(--logo)",
        inactive: false,
        hint: STATION_WAITING_HINT,
      };
    }
    case STATE_CHARGING:
      return {
        state: "заряжается",
        colorClass: "color-blue",
        icon: SVG.station_charge,
        color: "var(--logo)",
        inactive: false,
        hint: null,
      };
    case STATE_CHARGED:
      return {
        state: "заряжено",
        colorClass: "color-teal",
        icon: SVG.station_ready,
        color: "var(--logo)",
        inactive: false,
        hint: null,
      };

    default:
      // fallback — на случай неизвестного state_id
      return {
        state: "свободно",
        colorClass: "color-green",
        icon: SVG.station_free,
        color: "var(--green)",
        inactive: false,
        hint: null,
      };
  }
};
